import { Label } from "design-system/atoms/Label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { Loading } from "modules/shared/components/Loading"
import { useCallback, useEffect } from "react"
import { useTranslation } from "react-i18next"
import {
  CapabilitiesResponseDataSchema,
  CapabilityCampaignAssignation,
  CapabilityResponseDataSchema,
} from "schema"
import { toast } from "sonner"
import z from "zod"
import { useCapabilityProvider } from "../../hooks/useCapabilityProvider"
import { useInstallCapabilityMutation } from "../../hooks/useInstallCapabilityMutation"
import { useUpdateCapabilityConfigMutation } from "../../hooks/useUpdateCapabilityConfigMutation"
import { CapabilityOrganizationPhoneNumbers } from "./CapabilityOrganizationPhoneNumbers"

type Props = {
  capability: z.infer<typeof CapabilitiesResponseDataSchema>[number]
  mode?: "install" | "update"
  initialConfig?: { provider?: string }
  onClose: () => void
  onPendingChange?: (isPending: boolean) => void
  onConfigure?: (capability: z.infer<typeof CapabilityResponseDataSchema>) => void
  onSubmitHandlerRef?: React.MutableRefObject<
    | ((
        campaignAssignation: CapabilityCampaignAssignation,
        campaignIds: string[],
        campaignFlow?: "Inbound" | "Outbound",
      ) => Promise<void>)
    | null
  >
}

export function FindOrderNumberForm({
  capability,
  mode = "install",
  initialConfig,
  onClose,
  onPendingChange,
  onConfigure,
  onSubmitHandlerRef,
}: Props) {
  const { t } = useTranslation()
  const { mutateAsync: installCapability, isPending: isInstallPending } =
    useInstallCapabilityMutation()
  const { mutateAsync: updateCapabilityConfig, isPending: isUpdatePending } =
    useUpdateCapabilityConfigMutation()
  const isPending = mode === "install" ? isInstallPending : isUpdatePending

  const {
    provider,
    providerOptions,
    isIntegrationsStatusPending,
    organizationPhoneNumbers,
    setOrganizationPhoneNumbers,
  } = useCapabilityProvider({ capability, mode, initialConfig })

  useEffect(() => {
    onPendingChange?.(isPending || isIntegrationsStatusPending)
  }, [isPending, isIntegrationsStatusPending, onPendingChange])

  const handleSubmit = useCallback(
    async (
      campaignAssignation: CapabilityCampaignAssignation,
      campaignIds: string[],
      campaignFlow?: "Inbound" | "Outbound",
    ) => {
      if (isPending) return

      let installedCapability: z.infer<typeof CapabilityResponseDataSchema> | undefined
      const config = { provider: "Shopify", organizationPhoneNumbers }

      if (mode === "install") {
        installedCapability = await installCapability({
          identifier: capability.identifier,
          config,
          campaignAssignation,
          campaignIds,
          campaignFlow,
        })
        toast.success(
          t("capabilities.list.install.success", {
            capability: t(`capabilities.catalog.${capability.identifier}.name`),
          }),
        )
      } else {
        await updateCapabilityConfig({ id: capability.id, config })
      }

      if (onConfigure && installedCapability) onConfigure(installedCapability)
      else onClose()
    },
    [
      isPending,
      organizationPhoneNumbers,
      mode,
      installCapability,
      capability.identifier,
      capability.id,
      t,
      updateCapabilityConfig,
      onConfigure,
      onClose,
    ],
  )

  if (onSubmitHandlerRef) onSubmitHandlerRef.current = handleSubmit
  if (isIntegrationsStatusPending) return <Loading />

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-0.5">
        <Label>{t("capabilities.catalog.FindOrderNumber.config.provider")}</Label>
        <Select value={provider ?? "Shopify"} disabled>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {providerOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <CapabilityOrganizationPhoneNumbers
        organizationPhoneNumbers={organizationPhoneNumbers}
        setOrganizationPhoneNumbers={setOrganizationPhoneNumbers}
      />
    </div>
  )
}
